import { useTranslation } from "react-i18next";
import { SearchNormal1, Calendar2, Status, Add, Refresh2 } from "iconsax-react";
import { Button, Select, Input, DatePicker } from "@/shared/ui";
import type { Locale, DateRange } from "@/shared/utils";
import type { CombinedStatus } from "../../../types";
import type { StatusOption } from "../../../hooks";

interface Props {
  search: string;
  onSearchChange: (v: string) => void;
  dateRange: DateRange;
  onDateRangeChange: (r: DateRange) => void;
  statusFilter: CombinedStatus | null;
  onStatusChange: (v: CombinedStatus | null) => void;
  statusOptions: StatusOption[];
  locale: Locale;
  onGenerateClick: () => void;
  canGenerate: boolean;
  hasActiveFilters: boolean;
  onReset: () => void;
}

export default function TimesheetsFilter({
  search,
  onSearchChange,
  dateRange,
  onDateRangeChange,
  statusFilter,
  onStatusChange,
  statusOptions,
  locale,
  onGenerateClick,
  canGenerate,
  hasActiveFilters,
  onReset,
}: Props) {
  const { t } = useTranslation("TimesheetsPage");

  function handleDateRangeChange(v: Date | { start: Date | null; end: Date | null } | null) {
    if (!v || v instanceof Date) return;
    onDateRangeChange(v);
  }

  return (
    <div className="flex items-center justify-between gap-2 mb-7">
      <div className="flex items-center gap-2">
        <div className="min-w-48">
          <Input
            isSmall
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("filters.searchPlaceholder")}
            icon={<SearchNormal1 size={14} color="currentColor" />}
          />
        </div>

        <DatePicker
          value={dateRange}
          mode="range"
          onChange={handleDateRangeChange}
          locale={locale}
          placeholder={t("filters.allTime")}
          isSmall
          hasPeriodPicker
          width="min-w-40"
          icon={<Calendar2 size={16} color="currentColor" />}
          align="left"
        />

        <div className="min-w-40">
          <Select<CombinedStatus | null>
            options={statusOptions}
            placeholder={t("filters.allStatuses")}
            value={statusFilter}
            onChange={onStatusChange}
            isSmall
            icon={<Status size={16} color="currentColor" />}
            width="w-44"
          />
        </div>

        {hasActiveFilters && (
          <button
            className="px-3 h-8 flex justify-center items-center gap-2 content-base-low cursor-pointer"
            onClick={onReset}>
            <Refresh2 size={16} color="currentColor" />
            <span className="text-label-sm">{t("filters.reset")}</span>
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        {canGenerate && (
          <Button variant="primary" size="md" onClick={onGenerateClick}>
            <Add size={16} color="currentColor" />
            {t("filters.createTimesheet")}
          </Button>
        )}
      </div>
    </div>
  );
}

